// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { OrderTifMap } from '@shared/utils/Trading/OrderTifEnum';
import { InstrumentTypes } from '@shared/utils/Instruments/InstrumentTypes';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { RiskPlan } from '@shared/commons/cache/RiskPlan';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { HistoryOrderFormatter } from '@shared/commons/cache/Formatters/HistoryOrderFormatter';
import { OrderHistoryUtils } from '@shared/utils/Trading/OrderHistoryUtils';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { StringUtils } from '@shared/utils/StringUtils';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { ThemeManager } from '../misc/ThemeManager';
export class OrderHistoryItem extends BaseItem {
    constructor(orderHistory) {
        super();
        this.orderHistory = orderHistory;
        if ((orderHistory === null || orderHistory === void 0 ? void 0 : orderHistory.InstrumentStr) != null) {
            const [instrumentStr] = orderHistory.InstrumentStr.split(':');
            orderHistory.InstrumentStr = instrumentStr;
        }
    }
    getQuickTableCellForeground(column) {
        const colors = ThemeManager.colors;
        switch (column) {
            case 1:
                return colors.tableSymbolForeground;
            case 2:
                if (!isNullOrUndefined(this.orderHistory)) {
                    return HistoryOrderFormatter.IsLong(this.orderHistory) ? colors.buyColor : colors.sellColor;
                }
                else {
                    return super.getQuickTableCellForeground(column);
                }
            case 8:
                return colors.tableQuantityForeground;
            default:
                return super.getQuickTableCellForeground(column);
        }
    }
    ColumnCount() {
        return OrderHistoryItem.columnsParams.length;
    }
    // TODO. Refactor. Optimize.
    getColumnData(column) {
        const val = this.getColumnValue(column);
        let fVal = null;
        const orderHistory = this.orderHistory;
        const ins = this.GetCurrentInstrument();
        switch (column) {
            case 1:
                if (ins) {
                    fVal = ins.DisplayShortName();
                }
                break;
            case 2:
                fVal = Resources.getResource(HistoryOrderFormatter.IsLong(orderHistory) ? 'general.trading.Buy' : 'general.trading.Sell');
                break;
            case 5:
                fVal = orderHistory.OrderNumber;
                break;
            case 7:
            case 15:
                fVal =
                    !isNaN(val) && val !== null
                        ? ins
                            ? ins.formatPricePrecision(val)
                            : PriceFormatter.formatPrice(val, 2)
                        : '';
                break;
            case 11:
                fVal = DateTimeUtils.FormatToTime(val);
                break;
            case 12:
                fVal = DateTimeUtils.formatDate(val, 'DD.MM.YYYY  HH:mm:ss');
                break;
            case 18:
                fVal = HistoryOrderFormatter.GetBoughtStr(orderHistory);
                break;
            case 19:
                fVal = HistoryOrderFormatter.GetSoldStr(orderHistory);
                break;
            case 20:
                fVal = ins && val ? ins.formatPrice(val) : '';
                break;
            case 21:
                if (val && val.getTime() > 0) {
                    fVal = DateTimeUtils.FormatToDate(val);
                }
                else {
                    fVal = '';
                }
                break;
            case 29:
                fVal = val ? OrderUtils.GetFormattedLeverage(val, true) : StringUtils.NOT_AVAILABLE;
                break;
            default:
                if (val instanceof Date) {
                    fVal = DateTimeUtils.FormatToDateAndTime(val);
                }
                else if (val instanceof Number && ins) {
                    fVal = ins.formatPrice(val);
                }
        }
        if (!fVal && fVal !== '') {
            fVal = val ? val.toString() : '';
        }
        return new ColumnData(val, fVal);
    }
    getColumnValue(column) {
        var _a;
        const orderHistory = this.orderHistory;
        switch (column) {
            case 0: {
                return orderHistory.Account != null ? orderHistory.Account.toString() : orderHistory.AccountStr;
            }
            case 1: {
                return orderHistory.Instrument != null
                    ? orderHistory.Instrument.DisplayShortName()
                    : ((_a = orderHistory.InstrumentStr) !== null && _a !== void 0 ? _a : '');
            }
            // TODO. Ugly.
            case 2: {
                return orderHistory.BuySell;
            }
            case 3:
                return OrderHistoryUtils.localizeOrderExecutionType(orderHistory.eventTypeRaw);
            case 4: {
                return Resources.getResource(orderHistory.Message);
            }
            case 5: {
                const orderNumber = orderHistory.OrderNumber.split('-')[0];
                return parseFloat(orderNumber) || 0;
            }
            case 6: {
                return Resources.getResource('property.' + OrderUtils.getOrderTypeLocalizationKey(orderHistory.OrderType));
            }
            case 7: {
                return !isNaN(orderHistory.Price)
                    ? OrderUtils.GetCorrectPriceForOrderType(orderHistory.Price, orderHistory.StopLimit, OrderUtils.PriceType.LimitPrice, orderHistory.OrderType)
                    : NaN;
            }
            case 8: {
                let sign = 1;
                // if (!buy && sess.ShowAmountSign)
                //     sign = -1
                if (orderHistory.Quantity < 0 && sign < 0) {
                    sign = 1;
                }
                return WDSettingsUtils.displayAmountInLots() || !orderHistory.Instrument
                    ? orderHistory.Quantity * sign
                    : orderHistory.Quantity * sign * orderHistory.Instrument.LotSize;
            }
            case 9: {
                return orderHistory.Route;
            }
            case 10: {
                return orderHistory.RemoteId;
            }
            case 11: {
                return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(orderHistory.Time);
            }
            case 12: {
                return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(orderHistory.Date);
            }
            // при экспорте нам нужно знать значение в виде строки
            case 13: {
                if (orderHistory.Instrument && orderHistory.Instrument.InstrType === InstrumentTypes.FORWARD) {
                    return '';
                }
                else {
                    const tif = orderHistory.TimeInForce;
                    const type = tif ? tif.type : null;
                    const expirationTime = (tif === null || tif === void 0 ? void 0 : tif.expirationTime) ? ' ' + tif.expirationTime : '';
                    return Resources.getResource('property.' + OrderTifMap[type]) + expirationTime;
                }
            }
            case 14: {
                return orderHistory.TraderId;
            }
            case 15: {
                return orderHistory
                    ? OrderUtils.GetCorrectPriceForOrderType(orderHistory.Price, orderHistory.StopLimit, OrderUtils.PriceType.StopPrice, orderHistory.OrderType)
                    : NaN;
            }
            case 16: {
                const account = this.GetCurrentAccount();
                return account ? account.userLogin : '';
            }
            case 17: {
                if (orderHistory && orderHistory.Instrument) {
                    return orderHistory.Instrument.getTypeString();
                }
                else if (orderHistory === null || orderHistory === void 0 ? void 0 : orderHistory.SymbolType) {
                    return orderHistory.SymbolType;
                }
                return '';
            }
            case 18: {
                if (orderHistory && orderHistory.Instrument) {
                    return HistoryOrderFormatter.GetBought(orderHistory);
                }
                else {
                    return 0;
                }
            }
            case 19: {
                if (orderHistory && orderHistory.Instrument) {
                    return HistoryOrderFormatter.GetSold(orderHistory);
                }
                else {
                    return 0;
                }
            }
            case 20: {
                if (orderHistory && orderHistory.StrikePrice > 0) {
                    return orderHistory.StrikePrice;
                }
                else {
                    return 0;
                }
            }
            case 21: {
                if (!isNullOrUndefined(orderHistory === null || orderHistory === void 0 ? void 0 : orderHistory.Instrument) &&
                    orderHistory.Instrument.ExpDateReal.getFullYear() > 2000) {
                    return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(orderHistory.Instrument.ExpDateReal);
                }
                else {
                    return DateTimeUtils._ZeroTime;
                }
            }
            case 22: {
                let sign = 1;
                // if (!buy && sess.ShowAmountSign)
                //     sign = -1
                if (orderHistory.DisclosedQty < 0 && sign < 0) {
                    sign = 1;
                }
                if (WDSettingsUtils.displayAmountInLots() || !orderHistory.Instrument) {
                    return orderHistory.DisclosedQty * sign;
                }
                else {
                    return orderHistory.DisclosedQty * sign * orderHistory.Instrument.LotSize;
                }
            }
            case 23: {
                if (!WDSettingsUtils.displayAmountInLots() && orderHistory.Instrument) {
                    return orderHistory.RemainingQuantity * orderHistory.Instrument.LotSize;
                }
                else {
                    return orderHistory.RemainingQuantity;
                }
            }
            case 24: {
                if (!WDSettingsUtils.displayAmountInLots() && orderHistory.Instrument) {
                    return orderHistory.FilledQuantity * orderHistory.Instrument.LotSize;
                }
                else {
                    return orderHistory.FilledQuantity;
                }
            }
            case 25: {
                if (orderHistory.Instrument) {
                    return InstrumentUtils.GetLocalizedProductType(orderHistory.Instrument, orderHistory.ProductType);
                }
                else {
                    return '';
                }
            }
            case 26: {
                return (orderHistory === null || orderHistory === void 0 ? void 0 : orderHistory.ExternalOrderId) ? orderHistory.ExternalOrderId : '';
            }
            case 27: {
                if (orderHistory.Instrument) {
                    return orderHistory.Instrument.TradingExchange;
                }
                else if (orderHistory.TradingExchange) {
                    return orderHistory.TradingExchange;
                }
                return '';
            }
            case 28: {
                if (!orderHistory)
                    return '';
                let descr = orderHistory.Description;
                const ins = orderHistory.Instrument;
                if (!descr && ins) {
                    descr = ins.DescriptionValue();
                }
                return descr;
            }
            case 29:
                if (!orderHistory)
                    return NaN;
                return +orderHistory.Leverage;
            default:
                return '';
        }
    }
    getFormatTotalValue(column, value) {
        let formattedValue = '';
        switch (column) {
            case 8:
                formattedValue = value.toFixed(2);
                break;
            default:
                formattedValue = super.getFormatTotalValue(column, value);
        }
        return formattedValue;
    }
    // TODO. Refactor.
    GetColumnParams(column) {
        const result = OrderHistoryItem.columnsParams[column];
        result.Hidden = result.DefaultHidden;
        result.MenuIndex = this.GetMenuIndex(result.HeaderKey);
        // Форматирование цены
        if (column === 7) {
            result.FormatPrice = true;
        }
        if (column === 4 || column === 9 || column === 10 || column === 14 || column === 11) {
            result.Hidden = true;
        }
        // роут
        /* TODO. Rules.
    if (column === 9)
        result.Hidden = !this.dataCache.isAllowed(RulesSet.FUNCTION_POSITIONSADMIN, null);
    */
        // Product type
        if (column === 25) {
            result.Hidden = !RiskPlan.showProductType;
        }
        return result;
    }
    GetMenuIndex(headerKey) {
        const idx = OrderHistoryItem.ListMenuIndex.indexOf(headerKey);
        return idx === -1 ? 100 : idx;
    }
    // TODO. Remove?
    fireUpdate() { }
    GetCurrentAccount() {
        const orderHistory = this.orderHistory;
        return orderHistory ? orderHistory.Account : null;
    }
    GetCurrentInstrument() {
        const orderHistory = this.orderHistory;
        return orderHistory ? orderHistory.Instrument : null;
    }
    getTotalValuePrecision(column, totalCurrencyAfterFiltration) {
        switch (column) {
            case 8:
                return 2;
                break;
            default:
                return super.getTotalValuePrecision(column, totalCurrencyAfterFiltration);
        }
    }
}
OrderHistoryItem.ListMenuIndex = [
    'panel.OrderBook.Account',
    'panel.OrderBook.Login',
    'panel.OrderBook.Route',
    'panel.OrderBook.Instrument',
    'panel.OrderBook.instrType',
    'panel.OrderBook.OrderNumber',
    'panel.OrderBook.Action',
    'panel.OrderBook.Type',
    'panel.OrderBook.Price',
    'panel.OrderBook.Quantity',
    'panel.OrderBook.Date',
    'panel.OrderBook.Time',
    'panel.OrderBook.Event',
    'panel.OrderBook.TIF',
    'panel.OrderBook.StopPrice',
    'panel.OrderBook.TraderID',
    'panel.OrderBook.Bought',
    'panel.OrderBook.Sold',
    'panel.OrderBook.StrikePrice',
    'panel.OrderBook.ExpirationDate',
    'panel.OrderBook.ReminingQuantity',
    'panel.OrderBook.FilledQuantity',
    'panel.OrderBook.ExternalOrderID',
    'panel.OrderBook.ExchangeTrading',
    'panel.OrderBook.Description',
    'panel.OrderBook.Leverage'
];
OrderHistoryItem.columnsParams = [
    /* 0 */ new ColumnParams('panel.OrderBook.Account', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.OrderBook.Instrument', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true, false, true),
    new ColumnParams('panel.OrderBook.Action', 70, QTAlignment.Right, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.OrderBook.Event', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.OrderBook.Message', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    /* 5 */ new ColumnParams('panel.OrderBook.OrderNumber', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, false, true),
    new ColumnParams('panel.OrderBook.Type', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.OrderBook.Price', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.OrderBook.Quantity', 70, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.OrderBook.Route', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 10 */ new ColumnParams('panel.OrderBook.RemoteID', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.Time', 70, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.OrderBook.Date', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.OrderBook.TIF', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.TraderID', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 15 */ new ColumnParams('panel.OrderBook.StopPrice', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.OrderBook.Login', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.OrderBook.instrType', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.OrderBook.Bought', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.OrderBook.Sold', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    /* 20 */ new ColumnParams('panel.OrderBook.StrikePrice', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.ExpirationDate', 40, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.DislocedQty', 40, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.ReminingQuantity', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.OrderBook.FilledQuantity', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    /* 25 */ new ColumnParams('panel.OrderBook.ProductType', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.ExternalOrderID', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.ExchangeTrading', 70, QTAlignment.Left, TableTotalsVisibility.None, false),
    new ColumnParams('panel.OrderBook.Description', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.OrderBook.Leverage', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true)
    /* 30 */
];
